import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppConstants } from 'src/app/commons/app-constants';
import { NetworkStatusService } from '../network-status.service';

const IDLE_MS          = 5 * 60 * 1000; // 5 min idle → stop counting
const HEARTBEAT_MS     = 30_000;         // check tick every 30 s
const SLOW_CONN_MIN_GAP_MS = 5 * 60 * 1000; // on 2G, only actually send once per 5 min
const ACTIVITY_EVENTS  = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;

/**
 * Tracks user active time per Angular route and sends 30-second heartbeats
 * to the backend (POST /api/v1/analytics/heartbeat).
 * Start this from AppComponent.ngOnInit() after login is confirmed.
 */
@Injectable({ providedIn: 'root' })
export class ActivityTrackerService implements OnDestroy {

  private lastActivity  = Date.now();
  private lastSentAt    = 0;
  private currentRoute  = '/';
  private intervalId    = 0;
  private cleanupFns: Array<() => void> = [];
  private running       = false;
  private readonly networkStatusService = inject(NetworkStatusService);

  constructor(
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;

    // Activity listeners (passive, outside NgZone for perf)
    this.ngZone.runOutsideAngular(() => {
      for (const ev of ACTIVITY_EVENTS) {
        const fn = () => { this.lastActivity = Date.now(); };
        window.addEventListener(ev, fn, { passive: true });
        this.cleanupFns.push(() => window.removeEventListener(ev, fn));
      }

      // Flush on tab hide
      const onVisibility = () => { if (document.hidden) this.flush(); };
      document.addEventListener('visibilitychange', onVisibility);
      this.cleanupFns.push(() => document.removeEventListener('visibilitychange', onVisibility));

      // 30-second heartbeat
      this.intervalId = window.setInterval(() => this.tick(), HEARTBEAT_MS);
      this.cleanupFns.push(() => clearInterval(this.intervalId));
    });

    // Track route changes (Angular zone is fine here)
    const sub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
    ).subscribe((e: any) => {
      this.currentRoute = (e.urlAfterRedirects as string).split('?')[0];
    });
    this.cleanupFns.push(() => sub.unsubscribe());
  }

  stop(): void {
    this.cleanupFns.forEach(fn => fn());
    this.cleanupFns = [];
    this.running = false;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private tick(): void {
    if (!this.shouldSendNow()) return;
    const idleMs = Date.now() - this.lastActivity;
    if (idleMs < IDLE_MS) {
      this.sendHeartbeat(30);
    }
  }

  private flush(): void {
    if (!this.networkStatusService.isOnline) return;
    const idleMs = Date.now() - this.lastActivity;
    if (idleMs < IDLE_MS) {
      const seconds = Math.max(1, Math.min(30, Math.ceil(idleMs / 1000)));
      this.sendHeartbeat(seconds);
    }
  }

  // Offline: never send (nothing would arrive anyway). On a detected 2G/slow-2g
  // connection: coalesce to one request per SLOW_CONN_MIN_GAP_MS instead of every tick.
  private shouldSendNow(): boolean {
    if (!this.networkStatusService.isOnline) return false;
    if (this.networkStatusService.isSlowConnection) {
      return Date.now() - this.lastSentAt >= SLOW_CONN_MIN_GAP_MS;
    }
    return true;
  }

  private sendHeartbeat(seconds: number): void {
    this.lastSentAt = Date.now();
    this.ngZone.run(() => {
      this.http.post<void>(
        `${AppConstants.API_URL}api/v1/analytics/heartbeat`,
        { route: this.currentRoute, seconds },
        { withCredentials: true },
      ).subscribe({ error: () => {} });
    });
  }
}
